import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { adminUpdateWorkExtensionStatus } from "helpers/backend_helpers";
import { toast } from "react-toastify";
import {
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Button,
} from "reactstrap";
import Select from "react-select";
import { formatDate } from "helpers/globalFunction";

interface SelectOption {
  value: string;
  label: string;
}

interface WorkExtensionValues {
  note: string;
  status: SelectOption | null;
}

const WorkExtensionModalConfiguration = ({
  show,
  onCloseClick,
  getData,
  data,
}: any) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFullEmployeeNote, setShowFullEmployeeNote] = useState(false);
  const [showFullApprovalNote, setShowFullApprovalNote] = useState(false);

  const validation = useFormik<WorkExtensionValues>({
    enableReinitialize: true,
    initialValues: {
      note: "",
      status: null,
    },
    validationSchema: Yup.object({
      note: Yup.string().required("Catatan harus diisi!"),
      status: Yup.object()
        .shape({
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
        .nullable()
        .required("Status harus diisi!"),
    }),
    onSubmit: (values) => {
      if (!values.status?.value) {
        return;
      }
      setShowConfirmModal(true);
    },
  });

  const handleConfirmSubmit = () => {
    const formattedValues = {
      note: validation.values.note,
      status: validation.values.status?.value || "",
    };

    adminUpdateWorkExtensionStatus(data._id, formattedValues)
      .then((response: any) => {
        setShowConfirmModal(false);
        onCloseClick();
        getData();
        toast.success(
          <div>
            Data lembur <b>{response?.name}</b> berhasil diperbarui!
          </div>
        );
        validation.resetForm();
      })
      .catch(() => {
        setShowConfirmModal(false);
      });
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const comboStatus = [
    { value: "ACCEPTED", label: "DISETUJUI" },
    { value: "REJECTED", label: "DITOLAK" },
  ];

  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(13, 110, 253, 0.25)"
        : "none",
      "&:hover": {
        borderColor: "#0d6efd",
      },
      minHeight: "42px",
      borderRadius: "8px",
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
        ? "#e7f1ff"
        : "white",
      color: state.isSelected ? "white" : "#212529",
      "&:active": {
        backgroundColor: "#0d6efd",
      },
    }),
  };

  useEffect(() => {
    if (!show) {
      validation.resetForm();
      setShowConfirmModal(false);
      setShowFullEmployeeNote(false);
      setShowFullApprovalNote(false);
    }
  }, [show]);

  return (
    <>
      <Modal
        size="lg"
        fade={true}
        isOpen={show}
        toggle={onCloseClick}
        centered={true}
      >
        <ModalHeader className="border-0 pb-0" toggle={onCloseClick}>
          <div className="d-flex align-items-center">
            <i className="ri-time-line fs-4 me-2"></i>
            <span className="fw-bold">Konfirmasi Lembur</span>
          </div>
        </ModalHeader>
        <ModalBody className="p-4">
          <div
            className="card border-0 mb-4"
            style={{
              background:
                "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
              borderRadius: "12px",
            }}
          >
            <div className="card-body p-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="d-flex align-items-start">
                    <i className="ri-user-3-line text-primary me-2 mt-1"></i>
                    <div>
                      <small className="text-muted d-block mb-1">
                        Nama Karyawan
                      </small>
                      <strong className="fs-6">{data?.member?.name}</strong>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-start">
                    <i className="ri-shield-user-line text-primary me-2 mt-1"></i>
                    <div>
                      <small className="text-muted d-block mb-1">No. ID</small>
                      <strong className="fs-6">{data?.member?.noId}</strong>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-start">
                    <i className="ri-calendar-schedule-line text-primary me-2 mt-1"></i>
                    <div>
                      <small className="text-muted d-block mb-1">Shift</small>
                      <strong className="fs-6">
                        {data?.shift?.name}, {data?.shift?.checkInTime} -{" "}
                        {data?.shift?.checkOutTime}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-3 text-primary">
              <i className="ri-information-line me-2"></i>
              Detail Lembur
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <div
                  className="p-3 border rounded"
                  style={{ borderRadius: "10px", borderColor: "#e9ecef" }}
                >
                  <small className="text-muted d-block mb-1">
                    <i className="ri-calendar-event-line me-1"></i>
                    Tanggal Lembur
                  </small>
                  <strong>{formatDate(data?.extensionDate, "full")}</strong>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className="p-3 border rounded"
                  style={{ borderRadius: "10px", borderColor: "#e9ecef" }}
                >
                  <small className="text-muted d-block mb-1">
                    <i className="ri-timer-line me-1"></i>
                    Durasi Sebelum Shift
                  </small>
                  <strong>{data?.durationBeforeShiftMinutes} menit</strong>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className="p-3 border rounded"
                  style={{ borderRadius: "10px", borderColor: "#e9ecef" }}
                >
                  <small className="text-muted d-block mb-1">
                    <i className="ri-timer-flash-line me-1"></i>
                    Durasi Sesudah Shift
                  </small>
                  <strong>{data?.durationAfterShiftMinutes} menit</strong>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className="p-3 border rounded"
                  style={{ borderRadius: "10px", borderColor: "#e9ecef" }}
                >
                  <small className="text-muted d-block mb-1">
                    <i className="ri-time-line me-1"></i>
                    Total Durasi
                  </small>
                  <strong className="text-primary">
                    {(data?.durationBeforeShiftMinutes || 0) +
                      (data?.durationAfterShiftMinutes || 0)}{" "}
                    menit
                  </strong>
                </div>
              </div>
              <div className="col-12">
                <div
                  className="p-3 bg-light rounded"
                  style={{ borderRadius: "10px" }}
                >
                  <small className="text-muted d-block mb-2">
                    <i className="ri-message-3-line me-1"></i>
                    Keterangan Karyawan
                  </small>
                  <p className="mb-0">
                    {data?.note && data.note.length > 120 ? (
                      <>
                        {showFullEmployeeNote
                          ? data.note
                          : `${data.note.substring(0, 120)}...`}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-1 text-primary"
                          style={{
                            textDecoration: "none",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                          onClick={() =>
                            setShowFullEmployeeNote(!showFullEmployeeNote)
                          }
                        >
                          {showFullEmployeeNote
                            ? "Lihat Lebih Sedikit"
                            : "Lihat Selengkapnya"}
                        </button>
                      </>
                    ) : (
                      data?.note || "-"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {data?.status === "PENDING" ? (
            <div
              className="p-4 border rounded mb-3"
              style={{
                borderRadius: "12px",
                backgroundColor: "#f8f9fa",
                borderColor: "#dee2e6",
              }}
            >
              <h6 className="fw-bold mb-3 text-dark">
                <i className="ri-edit-box-line me-2"></i>
                Form Persetujuan
              </h6>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="status" className="form-label fw-semibold">
                    <i className="ri-checkbox-circle-line me-1"></i>
                    Status Persetujuan <span className="text-danger">*</span>
                  </Label>
                  <Select
                    value={validation.values.status}
                    onChange={(e: any) => {
                      validation.setFieldValue("status", e);
                    }}
                    options={comboStatus}
                    styles={customSelectStyles}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Pilih Status Persetujuan"
                    isClearable
                  />
                  {validation.errors.status && validation.touched.status ? (
                    <div className="text-danger small mt-1">
                      <i className="ri-error-warning-line me-1"></i>
                      {validation.errors.status}
                    </div>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label htmlFor="note" className="form-label fw-semibold">
                    <i className="ri-file-text-line me-1"></i>
                    Catatan Admin <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    className="form-control"
                    id="note"
                    name="note"
                    rows={4}
                    placeholder="Masukkan catatan atau alasan persetujuan/penolakan..."
                    value={validation.values.note || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.note && validation.errors.note
                        ? true
                        : false
                    }
                    style={{
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  {validation.touched.note && validation.errors.note ? (
                    <FormFeedback type="invalid">
                      <i className="ri-error-warning-line me-1"></i>
                      {validation.errors.note}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                  <button
                    type="button"
                    className="btn btn-light px-4 btn-sm"
                    onClick={onCloseClick}
                    style={{
                      borderRadius: "8px",
                      fontWeight: "500",
                    }}
                  >
                    <i className="ri-close-line me-1"></i>
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4 btn-sm"
                    style={{
                      borderRadius: "8px",
                      fontWeight: "500",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                    }}
                  >
                    <i className="ri-check-line me-1"></i>
                    Konfirmasi
                  </button>
                </div>
              </Form>
            </div>
          ) : (
            <div
              className="p-4 border rounded mb-3"
              style={{
                borderRadius: "12px",
                backgroundColor: "#f8f9fa",
                borderColor: "#dee2e6",
              }}
            >
              <h6 className="fw-bold mb-3 text-dark">
                <i className="ri-file-check-line me-2"></i>
                Catatan Persetujuan
              </h6>
              <div
                className="p-3 bg-white rounded"
                style={{ borderRadius: "10px" }}
              >
                <div className="mb-2">
                  <small className="text-muted d-block mb-1">
                    <i className="ri-checkbox-circle-line me-1"></i>
                    Status
                  </small>
                  <span
                    className={`badge ${
                      data?.status === "ACCEPTED" ? "bg-success" : "bg-danger"
                    }`}
                  >
                    {data?.status === "ACCEPTED" ? "DISETUJUI" : "DITOLAK"}
                  </span>
                </div>
                <div>
                  <small className="text-muted d-block mb-2">
                    <i className="ri-message-3-line me-1"></i>
                    Catatan Admin
                  </small>
                  <p className="mb-0">
                    {data?.noteApproval && data.noteApproval.length > 120 ? (
                      <>
                        {showFullApprovalNote
                          ? data.noteApproval
                          : `${data.noteApproval.substring(0, 120)}...`}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-1 text-primary"
                          style={{
                            textDecoration: "none",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                          onClick={() =>
                            setShowFullApprovalNote(!showFullApprovalNote)
                          }
                        >
                          {showFullApprovalNote
                            ? "Lihat Lebih Sedikit"
                            : "Lihat Selengkapnya"}
                        </button>
                      </>
                    ) : (
                      data?.noteApproval || "-"
                    )}
                  </p>
                </div>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <button
                  type="button"
                  className="btn btn-light px-4 btn-sm"
                  onClick={onCloseClick}
                  style={{
                    borderRadius: "8px",
                    fontWeight: "500",
                  }}
                >
                  <i className="ri-close-line me-1"></i>
                  Tutup
                </button>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={showConfirmModal}
        toggle={handleCancelConfirm}
        centered={true}
        size="sm"
      >
        <ModalHeader className="border-0 pb-2" toggle={handleCancelConfirm}>
          <div className="d-flex align-items-center">
            <i className="ri-question-line fs-4 me-2"></i>
            <span className="fw-bold">Konfirmasi</span>
          </div>
        </ModalHeader>
        <ModalBody className="p-4">
          <div className="text-center mb-4">
            <div
              className="mx-auto mb-3 d-flex align-items-center justify-content-center"
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
              }}
            >
              <i className="ri-alert-line fs-1 text-primary"></i>
            </div>
            <h5 className="fw-bold mb-2">Konfirmasi Perubahan</h5>
            <p className="text-muted mb-0">
              Apakah Anda yakin ingin{" "}
              {validation.values.status?.value === "ACCEPTED"
                ? "menyetujui"
                : "menolak"}{" "}
              pengajuan lembur ini?
            </p>
          </div>

          <div className="d-flex gap-2 justify-content-center">
            <Button
              color="light"
              onClick={handleCancelConfirm}
              className="px-4"
              style={{ borderRadius: "8px", fontWeight: "500" }}
              size="sm"
            >
              <i className="ri-close-line me-1"></i>
              Tidak
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              className="px-4"
              style={{
                borderRadius: "8px",
                fontWeight: "500",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
              size="sm"
            >
              <i className="ri-check-line me-1"></i>
              Ya, Lanjutkan
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default WorkExtensionModalConfiguration;
